import React, { useEffect, useRef } from 'react';
import { Card, Space, Statistic, Typography } from 'antd';
import PropTypes from 'prop-types';
import { Line } from 'react-chartjs-2';

import { FORMAT } from '../../../../utilities/functions';
import {
    calculateMedianScenario,
    calculateScenarioStats,
    getMedianLineColor
} from '../../utils/monte-carlo';

import './styles.scss';

const { Text } = Typography;

const EquityCurveChart = ({ scenarios, params }) => {
    const chartRef = useRef(null);

    const { scenario: medianScenario, index: medianIndex } = calculateMedianScenario(scenarios);
    const stats = calculateScenarioStats(scenarios);
    const medianColor = getMedianLineColor(medianScenario);

    useEffect(
        () => () => {
            if (chartRef.current) {
                chartRef.current.destroy();
            }
        },
        []
    );

    if (!scenarios || scenarios.length === 0) {
        return (
            <Card className="equity-chart" title="Equity Curve Simulation">
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No simulation data available</Text>
                </div>
            </Card>
        );
    }

    // Prepare chart data
    const labels = scenarios[0].map((point) => `Month ${point.month}`);

    const datasets = scenarios.map((scenario, index) => {
        const isMedian = index === medianIndex;

        return {
            label: isMedian ? 'Median Scenario' : `Scenario ${index + 1}`,
            data: scenario.map((point) => point.equity),
            borderColor: isMedian ? medianColor : 'rgba(158, 158, 158, 0.3)',
            backgroundColor: isMedian ? medianColor : 'rgba(158, 158, 158, 0.1)',
            borderWidth: isMedian ? 3 : 1,
            pointRadius: isMedian ? 3 : 0,
            pointHoverRadius: isMedian ? 5 : 3,
            tension: 0.4,
            fill: false,
            hidden: false,
            order: isMedian ? 0 : 1
        };
    });

    const chartData = {
        labels,
        datasets
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false
        },
        plugins: {
            legend: {
                display: true,
                position: 'top',
                labels: {
                    filter: (legendItem) => legendItem.text.includes('Median')
                }
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const value = context.parsed.y;
                        return `${context.dataset.label}: ${FORMAT.NUMBER_TO_CURRENCY(value)}`;
                    }
                }
            }
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Time (Months)'
                },
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.05)'
                }
            },
            y: {
                title: {
                    display: true,
                    text: 'Equity ($)'
                },
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.05)'
                },
                ticks: {
                    callback: (value) => FORMAT.NUMBER_TO_CURRENCY(value)
                }
            }
        }
    };

    const percentageReturn =
        ((stats.medianFinalEquity - params.startingEquity) / params.startingEquity) * 100;

    return (
        <Card className="equity-chart" title="Monte Carlo Equity Curve Simulation (20 Scenarios)">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Statistics Row */}
                <div className="statistics-row">
                    <Statistic
                        title="Starting Equity"
                        value={FORMAT.NUMBER_TO_CURRENCY(params.startingEquity)}
                        valueStyle={{ fontSize: 16 }}
                    />
                    <Statistic
                        title="Median Final Equity"
                        value={FORMAT.NUMBER_TO_CURRENCY(stats.medianFinalEquity)}
                        valueStyle={{ fontSize: 16, color: medianColor }}
                    />
                    <Statistic
                        title="Return"
                        value={`${percentageReturn.toFixed(2)}%`}
                        valueStyle={{
                            fontSize: 16,
                            color: percentageReturn >= 0 ? '#29a373' : '#E57373'
                        }}
                    />
                    <Statistic
                        title="Best Case"
                        value={FORMAT.NUMBER_TO_CURRENCY(stats.maxFinalEquity)}
                        valueStyle={{ fontSize: 16, color: '#29a373' }}
                    />
                    <Statistic
                        title="Worst Case"
                        value={FORMAT.NUMBER_TO_CURRENCY(stats.minFinalEquity)}
                        valueStyle={{ fontSize: 16, color: '#E57373' }}
                    />
                </div>

                {/* Chart */}
                <div className="chart-container">
                    <Line ref={chartRef} data={chartData} options={chartOptions} />
                </div>

                {/* Legend Info */}
                <div style={{ marginTop: 16 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        The median scenario is highlighted with a thicker line. Color indicates
                        direction:{' '}
                        <span style={{ color: '#29a373', fontWeight: 'bold' }}>Green</span>{' '}
                        (upward), <span style={{ color: '#000', fontWeight: 'bold' }}>Black</span>{' '}
                        (flat), <span style={{ color: '#E57373', fontWeight: 'bold' }}>Red</span>{' '}
                        (downward).
                    </Text>
                </div>
            </Space>
        </Card>
    );
};

EquityCurveChart.propTypes = {
    scenarios: PropTypes.arrayOf(
        PropTypes.arrayOf(
            PropTypes.shape({
                month: PropTypes.number.isRequired,
                equity: PropTypes.number.isRequired
            })
        )
    ).isRequired,
    params: PropTypes.shape({
        startingEquity: PropTypes.number.isRequired,
        winPercentage: PropTypes.number.isRequired,
        avgWinnerLoserRatio: PropTypes.number.isRequired,
        avgTradesPerMonth: PropTypes.number.isRequired,
        riskPerTrade: PropTypes.number.isRequired
    }).isRequired
};

export default EquityCurveChart;
